import { Controller } from "@hotwired/stimulus";

const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

export default class ImageFallbackController extends Controller {
  static values = { fallback: String, original: String };

  connect() {
    this.originalSource = this.element.dataset.imageRetrySource || this.element.currentSrc || this.element.src;
    this.element.dataset.imageRetrySource = this.originalSource;
    this.originalAlt = this.element.alt;
    this.retryCount = 0;
    this.retryTimer = null;
    this.retrying = false;
    this.usingFallback = false;

    if (this.element.complete) {
      if (this.element.naturalWidth === 0) {
        this.replace();
      } else {
        this.loaded();
      }
    }
  }

  disconnect() {
    clearTimeout(this.retryTimer);
  }

  replace() {
    if (!this.hasFallbackValue || this.usingFallback) return;
    if (this.retryTimer) return;

    this.showSkeleton();
    this.scheduleRetry();
  }

  scheduleRetry() {
    if (this.retryTimer) return;

    if (this.retryCount >= 3) {
      this.showFallback();
      return;
    }

    this.retryCount += 1;
    const delay = 500 * (2 ** (this.retryCount - 1));

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.element.isConnected) return;

      // Recover from a failed variant by requesting the original attachment.
      const source = this.hasOriginalValue && this.retryCount >= 2
        ? this.originalValue : this.originalSource;
      const url = new URL(source, document.baseURI);
      // Only refresh Rails URLs; changing signed storage URLs breaks signatures.
      if (url.origin === globalThis.location.origin && url.pathname.startsWith("/rails/active_storage/")) {
        url.searchParams.set("image_retry", `${Date.now()}-${this.retryCount}`);
      }
      this.element.removeAttribute("srcset");
      this.element.src = url.href;
    }, delay);
  }

  loaded() {
    if (!this.element.naturalWidth || this.element.src === TRANSPARENT_PIXEL) return;
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.retrying = false;
    this.element.alt = this.originalAlt;
    this.element.classList.remove("profile-image-skeleton");
    if (this.element.src !== this.fallbackValue) this.retryCount = 0;
  }

  showSkeleton() {
    this.retrying = true;
    this.element.alt = "";
    this.element.src = TRANSPARENT_PIXEL;
    this.element.classList.add("profile-image-skeleton");
  }

  showFallback() {
    this.retrying = false;
    this.usingFallback = true;
    this.element.alt = "";
    this.element.classList.remove("profile-image-skeleton");
    this.element.removeAttribute("srcset");
    this.element.src = this.fallbackValue;
  }
}
